import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  LayoutDashboard,
  Calendar,
  Users,
  Wrench,
  FileText,
  DollarSign,
  TrendingUp,
  Sparkles,
  Loader2,
  X,
  Plus,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  Brain,
  Cpu,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost, apiDelete, apiPatch } from "@/lib/apiAdmin";
import { useAdminAuth } from "@/lib/adminAuth";
import { toast } from "sonner";
import Logo from "@/components/landing/Logo";
import JarvisHUD from "@/components/admin/jarvis/JarvisHUD";
import { MoneyCards } from "@/components/admin/MoneyCards";
import { ServicesManager } from "@/components/admin/ServicesManager";
import { CustomersManager } from "@/components/admin/CustomersManager";
import { GoogleHub } from "@/components/admin/GoogleHub";
import { JarvisDock } from "@/components/admin/jarvis/JarvisDock";
import { fmt12 } from "@/lib/time";
import { AlertsBell } from "@/components/admin/AlertsBell";

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "calendar", label: "Calendar", Icon: Calendar },
  { key: "bookings", label: "Bookings", Icon: FileText },
  { key: "quotes", label: "Quotes", Icon: Sparkles },
  { key: "customers", label: "Customers", Icon: Users },
  { key: "services", label: "Services", Icon: Wrench },
  { key: "google", label: "Gmail & Reviews", Icon: Mail },
  { key: "knowledge", label: "Train AI", Icon: Brain },
  { key: "jarvis", label: "JARVIS", Icon: Cpu },
];

const STATUS_COLOR = {
  pending: "text-amber-400 bg-amber-500/10",
  confirmed: "text-emerald-400 bg-emerald-500/10",
  in_progress: "text-purple-300 bg-purple-500/15 animate-pulse",
  completed: "text-sky-400 bg-sky-500/10",
  cancelled: "text-red-400 bg-red-500/10",
};

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, loading, logout } = useAdminAuth();
  const [tab, setTab] = useState(() => { const q = new URLSearchParams(window.location.search); return q.get("google") || q.get("bank") ? "jarvis" : "overview"; });

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login", { replace: true });
  }, [loading, admin, navigate]);

  if (loading || !admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/5 bg-black/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 sm:gap-3 min-w-0"
            data-testid="admin-logo-btn"
          >
            <Logo size={32} showWordmark wordmarkClassName="text-xl" />
            <span className="text-gray-400 text-[10px] uppercase tracking-[0.3em] ml-1 hidden md:inline">
              Admin
            </span>
          </button>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <AlertsBell />
            <span className="text-sm text-gray-400 hidden lg:block max-w-[200px] truncate">{admin.email}</span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="admin-logout"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="text-gray-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/5 bg-[#0A0A0A]/95 backdrop-blur-xl sticky top-[70px] sm:top-[78px] z-20">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 flex gap-1 overflow-x-auto scrollbar-hide">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`relative flex items-center gap-2 px-3 sm:px-4 py-3 text-sm whitespace-nowrap transition-colors ${
                tab === key ? "text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
              {tab === key && (
                <motion.div
                  layoutId="tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-sky-500"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {tab === "overview" && <Overview />}
        {tab === "calendar" && <CalendarView />}
        {tab === "bookings" && <BookingsView />}
        {tab === "quotes" && <QuotesView />}
        {tab === "customers" && <CustomersManager />}
        {tab === "google" && <GoogleHub />}
        {tab === "services" && <ServicesView />}
        {tab === "knowledge" && <KnowledgeView />}
        {tab === "jarvis" && <JarvisHUD />}
      </main>
      {tab !== "jarvis" && <JarvisDock />}
    </div>
  );
}

// ---------- Overview ----------
function Overview() {
  const [data, setData] = useState(null);
  const [g, setG] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadG = () => apiGet("/admin/google-reviews/status").then(setG).catch(() => {});

  useEffect(() => {
    apiGet("/admin/analytics").then(setData);
    loadG();
  }, []);

  const refreshGoogle = async () => {
    setRefreshing(true);
    try {
      const res = await apiPost("/admin/google-reviews/refresh", {});
      if (res.ok) {
        toast.success(`Pulled ${res.count} review(s), ${res.photos} photo(s)`);
      } else {
        toast.error(res.reason || "Refresh failed");
      }
      await loadG();
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-sky-500" />;

  const cards = [
    { label: "Revenue", value: fmt(data.revenue), Icon: DollarSign, color: "text-emerald-400" },
    { label: "Deposits collected", value: fmt(data.deposits_collected), Icon: TrendingUp, color: "text-sky-400" },
    { label: "Paid bookings", value: data.paid_bookings, Icon: Check, color: "text-white" },
    { label: "Pending bookings", value: data.pending_bookings, Icon: FileText, color: "text-amber-400" },
    { label: "Avg ticket", value: fmt(data.avg_ticket), Icon: TrendingUp, color: "text-white" },
    { label: "Customers", value: data.total_customers, Icon: Users, color: "text-white" },
    { label: "Total quotes", value: data.total_quotes, Icon: Sparkles, color: "text-sky-400" },
    { label: "Abandoned quotes", value: data.abandoned_quotes, Icon: X, color: "text-red-400" },
  ];

  // Max revenue for sparkline scale
  const maxRev = Math.max(1, ...data.daily_revenue_30d.map((d) => d.revenue));

  return (
    <div className="space-y-6 sm:space-y-8">
      <MoneyCards />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {cards.map(({ label, value, Icon, color }) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-widest text-gray-500">{label}</p>
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <p className={`text-2xl font-['Outfit'] ${color}`}>{value}</p>
          </motion.div>
        ))}
      </div>

      {/* Google Reviews sync */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Google reviews sync</p>
            <p className="text-white text-sm">
              {g?.configured ? (
                <>
                  Configured · Cached: <span className="text-sky-400">{g.cached_count}</span>
                  {g.last_refresh && (
                    <span className="text-gray-500 ml-3">
                      Last refresh: {new Date(g.last_refresh).toLocaleString()}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-amber-400">
                  Not configured — set <code className="bg-white/5 px-1.5 py-0.5 rounded text-xs">GOOGLE_PLACES_API_KEY</code> in backend .env
                </span>
              )}
            </p>
          </div>
          <Button
            onClick={refreshGoogle}
            disabled={refreshing || !g?.configured}
            data-testid="refresh-google"
            className="bg-sky-500 hover:bg-sky-400 text-white"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh now"}
          </Button>
        </div>
      </div>

      {/* Revenue sparkline */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Last 30 days revenue</p>
        {data.daily_revenue_30d.length === 0 ? (
          <p className="text-gray-500 text-sm">No revenue yet — share your link and start booking.</p>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {data.daily_revenue_30d.map((d) => (
              <div
                key={d.date}
                title={`${d.date} — ${fmt(d.revenue)}`}
                className="flex-1 bg-gradient-to-t from-sky-500/40 to-sky-400 rounded-t hover:from-sky-400 hover:to-sky-300 transition-colors min-h-[3px]"
                style={{ height: `${(d.revenue / maxRev) * 100}%` }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Top services */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Top services by revenue</p>
        {data.top_services.length === 0 ? (
          <p className="text-gray-500 text-sm">No paid services yet.</p>
        ) : (
          <div className="space-y-2">
            {data.top_services.map((s) => (
              <div key={s.key} className="flex items-center justify-between py-2">
                <span className="text-white">{s.name}</span>
                <span className="flex items-center gap-4">
                  <span className="text-gray-500 text-sm">{s.count}×</span>
                  <span className="text-sky-400 font-semibold">{fmt(s.revenue)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Calendar ----------
function CalendarView() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [events, setEvents] = useState([]);
  const [external, setExternal] = useState([]);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({}); // { 'YYYY-MM-DD': {...} }
  const [loading, setLoading] = useState(false);
  const [createOn, setCreateOn] = useState(null); // YYYY-MM-DD
  const [editEvent, setEditEvent] = useState(null);
  const [editAvail, setEditAvail] = useState(null); // YYYY-MM-DD

  const monthName = new Date(month.y, month.m).toLocaleString("default", { month: "long", year: "numeric" });
  const first = new Date(month.y, month.m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();

  const load = async () => {
    setLoading(true);
    const fromStr = new Date(month.y, month.m, 1).toISOString().slice(0, 10);
    const toStr = new Date(month.y, month.m + 1, 0).toISOString().slice(0, 10);
    const [calData, svcData, availData] = await Promise.all([
      apiGet("/admin/calendar", { date_from: fromStr, date_to: toStr }),
      apiGet("/admin/services"),
      apiGet("/admin/availability", { start: fromStr, end: toStr }),
    ]);
    setEvents(calData.events);
    setExternal(calData.external_events || []);
    setServices(svcData.services);
    const map = {};
    (availData.availability || []).forEach((a) => { map[a.date] = a; });
    setAvailability(map);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month.y, month.m]);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      m[e.scheduled_date] = m[e.scheduled_date] || [];
      m[e.scheduled_date].push(e);
    });
    return m;
  }, [events]);

  const extByDay = useMemo(() => {
    const m = {};
    external.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [external]);

  const grid = [];
  for (let i = 0; i < startDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Outfit'] text-2xl">{monthName}</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            data-testid="cal-prev"
            onClick={() => setMonth((p) => p.m === 0 ? { y: p.y - 1, m: 11 } : { y: p.y, m: p.m - 1 })}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            data-testid="cal-next"
            onClick={() => setMonth((p) => p.m === 11 ? { y: p.y + 1, m: 0 } : { y: p.y, m: p.m + 1 })}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] sm:text-xs uppercase tracking-widest text-gray-500 mb-2">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="text-center py-2">
            <span className="hidden sm:inline">{d}</span>
            <span className="sm:hidden">{d.charAt(0)}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-sky-500 mx-auto" />
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {grid.map((d, i) => {
            const dateStr = d ? `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
            const evts = dateStr ? byDay[dateStr] || [] : [];
            const avail = dateStr ? availability[dateStr] : null;
            const isClosed = avail?.closed;
            return (
              <div
                key={`cell-${i}`}
                className={`group relative min-h-[64px] sm:min-h-[110px] rounded-lg border p-1 sm:p-2 text-[10px] sm:text-xs transition-colors ${
                  !d
                    ? "border-transparent"
                    : isClosed
                      ? "border-red-500/30 bg-red-500/5 cursor-pointer"
                      : "border-white/10 bg-white/[0.02] hover:border-sky-500/30 cursor-pointer"
                }`}
                onClick={() => { if (d) setCreateOn(dateStr); }}
                data-testid={d ? `cal-day-${dateStr}` : undefined}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-gray-500">{d}</p>
                      <button
                        onClick={(ev) => { ev.stopPropagation(); setEditAvail(dateStr); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] uppercase tracking-wider text-sky-400 hover:text-sky-300 hidden sm:inline-block"
                        title="Set availability"
                      >
                        Hours
                      </button>
                    </div>
                    {isClosed ? (
                      <div className="text-[10px] text-red-300 font-medium">CLOSED</div>
                    ) : (
                      <>
                        {avail?.open_hour !== undefined && (
                          <div className="text-[9px] text-sky-400/60 mb-0.5">
                            {String(avail.open_hour).padStart(2,'0')}:00–{String(avail.close_hour || 18).padStart(2,'0')}:00
                          </div>
                        )}
                        {evts.slice(0, 3).map((e) => (
                          <div
                            key={e.booking_id}
                            onClick={(ev) => { ev.stopPropagation(); setEditEvent(e); }}
                            title={`${fmt12(e.scheduled_slot)} — ${e.customer_name || e.customer_email || "Customer"} — ${fmt(e.total)}`}
                            className={`mb-0.5 sm:mb-1 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-[9px] sm:text-[10px] hover:opacity-80 cursor-pointer truncate ${STATUS_COLOR[e.status] || STATUS_COLOR.pending}`}
                          >
                            <span className="font-medium">{fmt12(e.scheduled_slot)}</span>
                            <span className="hidden sm:inline"> · {(e.customer_name || e.customer_name_override || (e.line_items || [])[0]?.name || e.vehicle?.model || "Booking").split(" ")[0]}</span>
                          </div>
                        ))}
                        {(extByDay[dateStr] || []).slice(0, 2).map((x, xi) => (
                          <div
                            key={`x-${xi}`}
                            title={`${x.calendar}: ${x.title} (${fmt12(x.time)})`}
                            data-testid={`cal-ext-${dateStr}-${xi}`}
                            className="mb-0.5 sm:mb-1 px-1 sm:px-2 py-0.5 rounded text-[9px] sm:text-[10px] truncate border-l-2 bg-white/[0.03] text-gray-300"
                            style={{ borderColor: x.color }}
                          >
                            <span className="font-medium">{fmt12(x.time)}</span>
                            <span className="hidden sm:inline"> · {x.title}</span>
                          </div>
                        ))}
                        {evts.length > 3 && (
                          <div className="text-[9px] text-gray-500 pl-1">+{evts.length - 3} more</div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {createOn && (
        <CalendarCreateModal
          date={createOn}
          services={services}
          onClose={() => setCreateOn(null)}
          onSaved={() => { setCreateOn(null); load(); }}
        />
      )}

      {editEvent && (
        <CalendarEventModal
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onChanged={() => { setEditEvent(null); load(); }}
        />
      )}

      {editAvail && (
        <AvailabilityModal
          date={editAvail}
          existing={availability[editAvail]}
          onClose={() => setEditAvail(null)}
          onSaved={() => { setEditAvail(null); load(); }}
        />
      )}
    </div>
  );
}

function AvailabilityModal({ date, existing, onClose, onSaved }) {
  const [closed, setClosed] = useState(existing?.closed || false);
  const [openHour, setOpenHour] = useState(existing?.open_hour ?? 9);
  const [closeHour, setCloseHour] = useState(existing?.close_hour ?? 18);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!closed && closeHour <= openHour) {
      toast.error("Close hour must be after open hour");
      return;
    }
    setSaving(true);
    try {
      await apiPost("/admin/availability", {
        date,
        closed,
        open_hour: closed ? null : Number(openHour),
        close_hour: closed ? null : Number(closeHour),
      });
      toast.success(closed ? "Day marked closed" : "Working hours saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await apiDelete(`/admin/availability/${date}`);
      toast.success("Reset to default schedule");
      onSaved();
    } catch (e) {
      toast.error("Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-5 sm:p-6 space-y-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white p-2 -m-2">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl">Working Hours</h3>
        <p className="text-sm text-gray-400">{date}</p>

        <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={closed}
            onChange={(e) => setClosed(e.target.checked)}
            className="w-4 h-4 accent-sky-500"
            data-testid="avail-closed"
          />
          <div>
            <p className="text-white text-sm">Mark this whole day as closed</p>
            <p className="text-xs text-gray-500">Customers won't be able to book any slot.</p>
          </div>
        </label>

        {!closed && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Open hour</label>
              <select
                value={openHour}
                onChange={(e) => setOpenHour(Number(e.target.value))}
                className="w-full bg-white/[0.03] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                data-testid="avail-open"
              >
                {[6,7,8,9,10,11,12,13,14].map(h => (
                  <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Close hour</label>
              <select
                value={closeHour}
                onChange={(e) => setCloseHour(Number(e.target.value))}
                className="w-full bg-white/[0.03] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                data-testid="avail-close"
              >
                {[12,13,14,15,16,17,18,19,20,21,22].map(h => (
                  <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/10">
          {existing ? (
            <Button variant="ghost" onClick={reset} disabled={saving} className="text-red-400 hover:bg-red-500/10">
              Reset to default
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="avail-save" className="bg-sky-500 hover:bg-sky-400 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function CalendarCreateModal({ date, services, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [picked, setPicked] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiGet("/slots", { date }).then((d) => setSlots(d.slots));
  }, [date]);

  const submit = async () => {
    if (!slot) { toast.error("Pick a time slot"); return; }
    setLoading(true);
    try {
      await apiPost("/admin/bookings", {
        customer_name: name,
        customer_email: email,
        scheduled_date: date,
        scheduled_slot: slot,
        services: picked,
        notes,
      });
      toast.success("Appointment added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-5 sm:p-6 space-y-3 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white p-2 -m-2">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl">New appointment</h3>
        <p className="text-sm text-gray-400">{date}</p>

        <Input placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} className="bg-white/[0.03] border-white/10 text-white" />
        <Input type="email" placeholder="Customer email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/[0.03] border-white/10 text-white" />

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-2">Time slot</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slots.length === 0 ? (
              <p className="col-span-3 sm:col-span-5 text-gray-500 text-sm">No slots open</p>
            ) : slots.map((s) => (
              <button key={s} onClick={() => setSlot(s)} className={`py-2 rounded text-sm ${slot === s ? "bg-sky-500 text-white" : "bg-white/[0.03] text-gray-300"}`}>
                {fmt12(s)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-2">Services (optional)</label>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {services.map((s) => {
              const sel = picked.includes(s.key);
              return (
                <button
                  key={s.key}
                  onClick={() => setPicked((p) => sel ? p.filter((x) => x !== s.key) : [...p, s.key])}
                  className={`w-full flex justify-between p-2 rounded text-sm transition-colors ${sel ? "bg-sky-500/15 text-white" : "bg-white/[0.02] text-gray-400 hover:text-white"}`}
                >
                  <span>{s.name}</span>
                  <span className="text-gray-500">${s.base}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white/[0.03] border-white/10 text-white" />

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="cal-create-submit" className="bg-sky-500 hover:bg-sky-400 text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add appointment"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function CalendarEventModal({ event, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);

  const update = async (patch) => {
    setBusy(true);
    try {
      await apiPatch(`/admin/bookings/${event.booking_id}`, patch);
      toast.success("Updated");
      onChanged();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this appointment? This cannot be undone.")) return;
    setBusy(true);
    try {
      await apiDelete(`/admin/bookings/${event.booking_id}`);
      toast.success("Deleted");
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d0d] p-5 sm:p-6 space-y-3 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white p-2 -m-2">
          <X className="w-5 h-5" />
        </button>
        <p className="text-xs uppercase tracking-widest text-gray-500">{event.scheduled_date} · {fmt12(event.scheduled_slot)}</p>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl break-words">{event.customer_name || event.customer_email || "Appointment"}</h3>
        <p className="text-sm text-gray-400">
          {(event.line_items || []).map((it) => it.name).join(" + ") || "—"}
        </p>
        <p className="text-sky-400 text-lg font-semibold">{fmt(event.total)}</p>

        <div className="grid grid-cols-2 gap-2 pt-2">
          {event.status !== "confirmed" && (
            <Button onClick={() => update({ status: "confirmed" })} disabled={busy} className="bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25">Confirm</Button>
          )}
          {event.status !== "completed" && (
            <Button onClick={() => update({ status: "completed" })} disabled={busy} className="bg-sky-500/15 text-sky-300 hover:bg-sky-500/25">Mark Done</Button>
          )}
          {event.status !== "cancelled" && (
            <Button onClick={() => update({ status: "cancelled" })} disabled={busy} className="bg-amber-500/15 text-amber-300 hover:bg-amber-500/25">Cancel</Button>
          )}
          <Button onClick={remove} disabled={busy} className="bg-red-500/15 text-red-300 hover:bg-red-500/25">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------- Bookings ----------
function BookingsView() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [rescheduleFor, setRescheduleFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await apiGet("/admin/bookings", statusFilter ? { status: statusFilter } : {});
    setBookings(data.bookings);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const update = async (id, patch) => {
    await apiPatch(`/admin/bookings/${id}`, patch);
    toast.success("Updated");
    load();
  };

  const markPaid = async (b) => {
    const method = window.prompt('Payment method (cash / card / other)?', 'cash');
    if (!method) return;
    try {
      await apiPost(`/admin/bookings/${b.booking_id}/mark-paid`, { method, amount: b.total });
      toast.success("Marked as paid");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    }
  };

  const startService = async (id) => {
    try {
      await apiPost(`/admin/bookings/${id}/start`, {});
      toast.success("Service started — customer notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const stopService = async (id) => {
    try {
      await apiPost(`/admin/bookings/${id}/stop`, {});
      toast.success("Service complete — customer notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const acceptCustomerProposal = async (id) => {
    try {
      await apiPost(`/admin/bookings/${id}/reschedule/accept`, {});
      toast.success("Reschedule approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const declineCustomerProposal = async (id) => {
    await apiDelete(`/admin/bookings/${id}/reschedule`);
    toast("Proposal declined");
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1">
        {["", "pending", "confirmed", "completed", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`shrink-0 px-3 py-1.5 rounded-md text-sm capitalize transition-colors ${
              statusFilter === s ? "bg-sky-500 text-white" : "bg-white/[0.03] text-gray-400 hover:text-white"
            }`}
          >
            {s || "all"}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
      ) : bookings.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No bookings</p>
      ) : (
        <div className="space-y-2">
          {bookings.map((b) => (
            <div key={b.booking_id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_COLOR[b.status] || STATUS_COLOR.pending}`}>
                      {b.status}
                    </span>
                    <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${b.payment_status === "paid" ? "text-emerald-400 bg-emerald-500/10" : "text-gray-400 bg-white/5"}`}>
                      {b.payment_status}
                    </span>
                    {b.reschedule_proposal && (
                      <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded text-sky-300 bg-sky-500/10">
                        reschedule proposed
                      </span>
                    )}
                    {b.awaiting_deposit && (
                      <span data-testid={`awaiting-deposit-${b.booking_id}`} className="text-xs uppercase tracking-widest px-2 py-0.5 rounded text-amber-300 bg-amber-500/10">
                        awaiting deposit
                      </span>
                    )}
                    {b.created_by === "jarvis_phone" && (
                      <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded text-cyan-300 bg-cyan-500/10">booked by phone</span>
                    )}
                  </div>
                  <p className="text-white font-medium">
                    {b.line_items.map((it) => it.name).join(" + ")}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {b.scheduled_date} · {fmt12(b.scheduled_slot)} — {b.customer_name || b.customer_name_override || b.customer_email || (b.line_items || [])[0]?.name || "Booking"}
                    {b.customer_phone && <span className="text-sky-300/70"> · {b.customer_phone}</span>}
                  </p>
                  {b.service_address && <p className="text-xs text-gray-500 mt-1">Location: {b.service_address}</p>}
                  {b.reschedule_proposal && (
                    <p className="text-xs text-sky-300 mt-1">
                      {b.reschedule_proposal.proposed_by === "customer" ? "Customer proposed: " : "Proposed: "}
                      {b.reschedule_proposal.new_date} · {fmt12(b.reschedule_proposal.new_slot)}
                      {b.reschedule_proposal.message ? ` — "${b.reschedule_proposal.message}"` : ""}
                    </p>
                  )}
                  {b.vehicle && (b.vehicle.make || b.vehicle.model) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {[b.vehicle.year, b.vehicle.color, b.vehicle.make, b.vehicle.model].filter(Boolean).join(" ")}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sky-400 font-semibold text-lg">{fmt(b.total)}</p>
                  <p className="text-xs text-gray-500">dep {fmt(b.deposit)}</p>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {b.reschedule_proposal?.proposed_by === "customer" && (
                    <>
                      <Button
                        size="sm"
                        data-testid={`accept-proposal-${b.booking_id}`}
                        onClick={() => acceptCustomerProposal(b.booking_id)}
                        className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => declineCustomerProposal(b.booking_id)}
                        className="text-red-400 hover:bg-red-500/10"
                      >
                        Decline
                      </Button>
                    </>
                  )}
                  {b.status !== "completed" && b.status !== "cancelled" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`reschedule-${b.booking_id}`}
                      onClick={() => setRescheduleFor(b)}
                      className="text-sky-400 hover:bg-sky-500/10"
                    >
                      Reschedule
                    </Button>
                  )}
                  {b.payment_status !== "paid" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`mark-paid-${b.booking_id}`}
                      onClick={() => markPaid(b)}
                      className="text-emerald-400 hover:bg-emerald-500/10"
                    >
                      Mark Paid
                    </Button>
                  )}
                  {b.status === "pending" || b.status === "confirmed" ? (
                    <Button
                      size="sm"
                      data-testid={`start-${b.booking_id}`}
                      onClick={() => startService(b.booking_id)}
                      className="bg-sky-500 hover:bg-sky-400 text-white"
                    >
                      ▶ Start
                    </Button>
                  ) : null}
                  {b.status === "in_progress" && (
                    <Button
                      size="sm"
                      data-testid={`stop-${b.booking_id}`}
                      onClick={() => stopService(b.booking_id)}
                      className="bg-emerald-500 hover:bg-emerald-400 text-white"
                    >
                      ■ Finish
                    </Button>
                  )}
                  {b.status !== "confirmed" && b.status !== "in_progress" && b.status !== "completed" && (
                    <Button size="sm" variant="ghost" onClick={() => update(b.booking_id, { status: "confirmed" })} className="text-emerald-400 hover:bg-emerald-500/10">Confirm</Button>
                  )}
                  {b.status !== "cancelled" && b.status !== "completed" && (
                    <Button size="sm" variant="ghost" onClick={() => update(b.booking_id, { status: "cancelled" })} className="text-red-400 hover:bg-red-500/10">Cancel</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {rescheduleFor && (
        <RescheduleModal
          booking={rescheduleFor}
          onClose={() => setRescheduleFor(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function RescheduleModal({ booking, onClose, onSaved }) {
  const [newDate, setNewDate] = useState(booking.scheduled_date);
  const [newSlot, setNewSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [message, setMessage] = useState("");
  const [validHours, setValidHours] = useState(72);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!newDate) return;
    apiGet("/slots", { date: newDate }).then((d) => {
      // include all slots; admin can pick any open one
      const all = d.all || d.slots;
      setSlots(all);
    });
  }, [newDate]);

  const submit = async () => {
    if (!newDate || !newSlot) {
      toast.error("Pick a date and time slot");
      return;
    }
    setLoading(true);
    try {
      await apiPost(`/admin/bookings/${booking.booking_id}/reschedule`, {
        new_date: newDate,
        new_slot: newSlot,
        message,
        valid_hours: Number(validHours),
      });
      toast.success("Reschedule proposal sent");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const cancelProposal = async () => {
    setLoading(true);
    try {
      await apiDelete(`/admin/bookings/${booking.booking_id}/reschedule`);
      toast.success("Proposal cancelled");
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-5 sm:p-6 space-y-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white p-2 -m-2">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl">Propose new time</h3>
        <p className="text-sm text-gray-400">
          Currently: <span className="text-white">{booking.scheduled_date} · {fmt12(booking.scheduled_slot)}</span>
        </p>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">New date</label>
          <Input
            type="date"
            value={newDate}
            onChange={(e) => { setNewDate(e.target.value); setNewSlot(""); }}
            className="bg-white/[0.03] border-white/10 text-white"
            data-testid="reschedule-date"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-2">New time slot</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slots.map((s) => (
              <button
                key={s}
                data-testid={`reschedule-slot-${s}`}
                onClick={() => setNewSlot(s)}
                className={`py-2 rounded text-sm transition-colors ${
                  newSlot === s ? "bg-sky-500 text-white" : "bg-white/[0.03] text-gray-300 hover:bg-white/10"
                }`}
              >
                {fmt12(s)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Valid for (hours)</label>
          <Input
            type="number"
            value={validHours}
            onChange={(e) => setValidHours(e.target.value)}
            className="bg-white/[0.03] border-white/10 text-white"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Message to customer (optional)</label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="bg-white/[0.03] border-white/10 text-white"
            placeholder="Sorry, we need to move your appointment to..."
          />
        </div>

        <div className="flex justify-between gap-2 pt-2">
          {booking.reschedule_proposal && (
            <Button variant="ghost" onClick={cancelProposal} disabled={loading} className="text-red-400 hover:bg-red-500/10">
              Cancel proposal
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button onClick={submit} disabled={loading} data-testid="reschedule-submit" className="bg-sky-500 hover:bg-sky-400 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send proposal"}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ---------- Quotes + Counter Offer ----------
function QuotesView() {
  const [quotes, setQuotes] = useState([]);
  const [abandonedOnly, setAbandonedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [counterFor, setCounterFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await apiGet("/admin/quotes", { abandoned_only: abandonedOnly });
    setQuotes(data.quotes);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [abandonedOnly]);

  const viewDetail = async (q) => {
    try {
      const full = await apiGet(`/admin/quotes/${q.quote_id}`);
      setDetailFor(full);
    } catch (e) {
      toast.error("Failed to load quote detail");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setAbandonedOnly(false)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${!abandonedOnly ? "bg-sky-500 text-white" : "bg-white/[0.03] text-gray-400"}`}
          >
            All quotes
          </button>
          <button
            onClick={() => setAbandonedOnly(true)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${abandonedOnly ? "bg-sky-500 text-white" : "bg-white/[0.03] text-gray-400"}`}
          >
            Abandoned only
          </button>
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
      ) : quotes.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No quotes</p>
      ) : (
        <div className="space-y-2">
          {quotes.map((q) => (
            <div key={q.quote_id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${q.booked ? "text-emerald-400 bg-emerald-500/10" : "text-amber-400 bg-amber-500/10"}`}>
                      {q.booked ? "booked" : "abandoned"}
                    </span>
                    <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded text-gray-400 bg-white/5 capitalize">
                      {q.dirtiness} · {q.dirtiness_score}/10
                    </span>
                    {q.counter_offer && (
                      <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded text-sky-300 bg-sky-500/10">
                        counter sent
                      </span>
                    )}
                  </div>
                  <p className="text-white font-medium">
                    {(q.line_items || []).map((it) => it.name).join(" + ") || "—"}
                  </p>
                  <p className="text-sm text-gray-400 mt-0.5">
                    {q.customer_name || q.customer_email || "anonymous"}
                    {q.customer_phone && <span className="text-sky-300/70"> · {q.customer_phone}</span>}
                    {" — "}
                    {new Date(q.created_at).toLocaleString()}
                  </p>
                  {q.notes && (
                    <p className="text-xs text-gray-500 mt-1 italic">"{q.notes}"</p>
                  )}
                  {q.counter_offer && (
                    <p className="text-xs text-sky-300 mt-1">
                      Counter: {fmt(q.counter_offer.new_total)} (was {fmt(q.counter_offer.original_total)})
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sky-400 font-semibold text-lg">{fmt(q.total)}</p>
                  <p className="text-xs text-gray-500">dep {fmt(q.deposit)}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`view-quote-${q.quote_id}`}
                    onClick={() => viewDetail(q)}
                    className="text-sky-400 hover:bg-sky-500/10"
                  >
                    View photos
                  </Button>
                  {!q.booked && (
                    <Button
                      data-testid={`counter-${q.quote_id}`}
                      size="sm"
                      onClick={() => setCounterFor(q)}
                      className="bg-sky-500/20 text-sky-200 hover:bg-sky-500/30"
                    >
                      Counter offer
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {counterFor && <CounterOfferModal quote={counterFor} onClose={() => setCounterFor(null)} onSaved={load} />}
      {detailFor && <QuoteDetailModal quote={detailFor} onClose={() => setDetailFor(null)} />}
    </div>
  );
}

function QuoteDetailModal({ quote, onClose }) {
  const photos = quote.photos_base64 || [];
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-2xl rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-5 sm:p-6 space-y-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white p-2 -m-2">
          <X className="w-5 h-5" />
        </button>

        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500">Quote · {new Date(quote.created_at).toLocaleString()}</p>
          <h3 className="font-['Outfit'] text-xl sm:text-2xl text-white">
            {quote.customer_name || quote.customer_email || "Anonymous customer"}
          </h3>
          {quote.customer_phone && (
            <p className="text-sm text-sky-300">{quote.customer_phone}</p>
          )}
          {quote.customer_email && (
            <p className="text-sm text-gray-400">{quote.customer_email}</p>
          )}
        </div>

        {quote.vehicle && (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Vehicle</p>
            <p className="text-sm text-white">
              {[quote.vehicle.year, quote.vehicle.color, quote.vehicle.make, quote.vehicle.model].filter(Boolean).join(" ") || "—"}
            </p>
          </div>
        )}

        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Services requested</p>
          <p className="text-sm text-white">
            {(quote.line_items || []).map((it) => `${it.name} ($${it.price})`).join(" + ") || "—"}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Dirtiness: <span className="capitalize text-amber-300">{quote.dirtiness}</span> ({quote.dirtiness_score}/10)
          </p>
        </div>

        {quote.notes && (
          <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3">
            <p className="text-[10px] uppercase tracking-widest text-sky-300 mb-1">Customer notes</p>
            <p className="text-sm text-white italic">"{quote.notes}"</p>
          </div>
        )}

        {quote.ai_summary && (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">AI Assessment</p>
            <p className="text-sm text-gray-300">{quote.ai_summary}</p>
          </div>
        )}

        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Photos uploaded ({photos.length})
          </p>
          {photos.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No photos uploaded with this quote.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <a
                  key={`photo-${i}`}
                  href={`data:image/jpeg;base64,${p}`}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-sky-500/50 transition-colors"
                  data-testid={`quote-photo-${i}`}
                >
                  <img
                    src={`data:image/jpeg;base64,${p}`}
                    alt={`Quote upload ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
          <div>
            <p className="text-xs text-gray-400">Quoted total</p>
            <p className="text-2xl text-sky-400 font-semibold">{fmt(quote.total)}</p>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Close</Button>
        </div>
      </motion.div>
    </div>
  );
}

function CounterOfferModal({ quote, onClose, onSaved }) {
  const [newTotal, setNewTotal] = useState(() => Math.round(quote.total * 0.9));
  const [message, setMessage] = useState("");
  const [validHours, setValidHours] = useState(48);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await apiPost(`/admin/quotes/${quote.quote_id}/counter-offer`, {
        new_total: Number(newTotal),
        message,
        valid_hours: Number(validHours),
      });
      toast.success("Counter offer saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save counter offer");
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    setLoading(true);
    try {
      await apiDelete(`/admin/quotes/${quote.quote_id}/counter-offer`);
      toast.success("Counter offer removed");
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-[#0d0d0d] p-5 sm:p-6 space-y-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white p-2 -m-2">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl">Counter offer</h3>
        <p className="text-sm text-gray-400">
          Original total: <span className="text-white">{fmt(quote.total)}</span>
        </p>

        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">New total ($)</label>
          <Input type="number" value={newTotal} onChange={(e) => setNewTotal(e.target.value)} className="bg-white/[0.03] border-white/10 text-white" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Valid for (hours)</label>
          <Input type="number" value={validHours} onChange={(e) => setValidHours(e.target.value)} className="bg-white/[0.03] border-white/10 text-white" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">Message to customer (optional)</label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="bg-white/[0.03] border-white/10 text-white" placeholder="We'd love to earn your business..." />
        </div>
        <div className="rounded-lg p-3 bg-sky-500/10 text-sm text-sky-200">
          New deposit (20%): <span className="font-semibold">{fmt(newTotal * 0.2)}</span>
        </div>
        <div className="flex justify-between gap-2 pt-2">
          {quote.counter_offer && (
            <Button variant="ghost" onClick={remove} disabled={loading} className="text-red-400 hover:bg-red-500/10">
              Remove
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button onClick={submit} disabled={loading} className="bg-sky-500 hover:bg-sky-400 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save counter offer"}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ---------- Customers ----------
function ServicesView() {
  return <ServicesManager />;
}

// ===========================================================================
// Train AI — knowledge base
// ===========================================================================
const KNOWLEDGE_CATEGORIES = [
  { key: "general", label: "General", color: "bg-white/10 text-white" },
  { key: "pricing", label: "Pricing", color: "bg-emerald-500/15 text-emerald-300" },
  { key: "services", label: "Services", color: "bg-sky-500/15 text-sky-300" },
  { key: "policy", label: "Policy", color: "bg-amber-500/15 text-amber-300" },
  { key: "issue", label: "Website Issue", color: "bg-red-500/15 text-red-300" },
];

function KnowledgeView() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", content: "", category: "general" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiGet("/admin/knowledge");
      setEntries(d.entries || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content required");
      return;
    }
    setSaving(true);
    try {
      await apiPost("/admin/knowledge", form);
      toast.success("Knowledge saved — customer AI now knows this");
      setForm({ title: "", content: "", category: form.category });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    try {
      await apiDelete(`/admin/knowledge/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-sky-500/20 bg-gradient-to-br from-sky-500/10 to-transparent p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <Brain className="w-6 h-6 text-sky-300 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-['Outfit'] text-xl sm:text-2xl text-white">Train Your AI</h2>
            <p className="text-sm text-gray-400 mt-1">
              Teach your customer-facing Q&amp;A bot facts about your business — pricing tiers, service area, policies,
              warranty terms, or website issues you've spotted. Entries here are injected into every customer Q&amp;A response.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <Input
            data-testid="knowledge-title"
            placeholder="Title (e.g. 'Express Wash pricing' or 'Trip charge over 30 miles')"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-black/40 border-white/10 text-white"
          />
          <Textarea
            data-testid="knowledge-content"
            placeholder="What should the AI know? Be specific — e.g. 'Our Express Wash is $69 for sedans, $89 for SUVs/trucks. Includes hand wash, tire shine, interior wipedown.'"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={4}
            className="bg-black/40 border-white/10 text-white resize-none"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1.5 flex-wrap">
              {KNOWLEDGE_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setForm({ ...form, category: c.key })}
                  className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                    form.category === c.key ? c.color : "bg-white/5 text-gray-400 hover:bg-white/10"
                  }`}
                  data-testid={`knowledge-cat-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="knowledge-save"
              className="bg-sky-500 hover:bg-sky-400 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Add to AI</>}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">
          {entries.length} knowledge {entries.length === 1 ? "entry" : "entries"}
        </p>
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-sky-500 mx-auto" />
        ) : entries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center text-sm text-gray-500">
            No entries yet — the AI is using its default knowledge. Add your first fact above.
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => {
              const cat = KNOWLEDGE_CATEGORIES.find((c) => c.key === e.category) || KNOWLEDGE_CATEGORIES[0];
              return (
                <div
                  key={e.knowledge_id}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3"
                  data-testid={`knowledge-entry-${e.knowledge_id}`}
                >
                  <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${cat.color}`}>
                    {cat.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{e.title}</p>
                    <p className="text-gray-400 text-sm mt-1 whitespace-pre-wrap">{e.content}</p>
                    <p className="text-[10px] text-gray-600 mt-2">
                      {new Date(e.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(e.knowledge_id)}
                    className="text-gray-500 hover:text-red-400 transition-colors p-1"
                    data-testid={`knowledge-delete-${e.knowledge_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
